import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import { PreKSettings as KSettings, DiagnosticAssessmentResult } from '../../types/kAssessmentTypes';
import { useKTTS, K_QUESTION_SCREEN_AUDIO } from '../../hooks/useKAudio';
import { useAuth } from '../../context/AuthContext';
import { progressQueue } from '../../services/ProgressQueueService';
import { recordPageTransition, completePreKAssessment } from '../../config/api';
import { FactStatus } from '../../types/progress';
import { TRACK_RANGES } from '../../types/constants';
import WarmupTransitionScreen from './WarmupTransitionScreen';
import { AnswerChoicesGrid, AssessmentHeader } from '../PreKAssessment/shared';
import ListenButton from '../common/ListenButton';

interface PartWholeQuestionScreenProps {
  settings: KSettings;
  onComplete: (results: DiagnosticAssessmentResult) => void;
}

const PartWholeQuestionScreen: React.FC<PartWholeQuestionScreenProps> = ({ settings, onComplete }) => {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [wallClockTimeRemaining, setWallClockTimeRemaining] = useState(60000); // 60 seconds wall clock limit
  const [wallClockStarted, setWallClockStarted] = useState(false); // Flag to track when wall clock starts
  const [activeTimeElapsed, setActiveTimeElapsed] = useState(0); // Track actual active thinking time
  const [questionTimeRemaining, setQuestionTimeRemaining] = useState(6000); // 6 seconds per question
  const [questionTimerActive, setQuestionTimerActive] = useState(false); // Per-question timer
  const [isWarmup, setIsWarmup] = useState(true);
  const [warmupCount, setWarmupCount] = useState(0);
  const [showTransitionScreen, setShowTransitionScreen] = useState(false);
  const [currentEmptyCount, setCurrentEmptyCount] = useState<number | null>(null);
  const [waitingForCorrectAnswer, setWaitingForCorrectAnswer] = useState(false);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [correctAnswers, setCorrectAnswers] = useState(0);
  const [timerActive, setTimerActive] = useState(false);
  const [ttsHasPlayed, setTtsHasPlayed] = useState(false);
  const [wrongAnswer, setWrongAnswer] = useState<number | null>(null);
  const [hasAnswered, setHasAnswered] = useState(false);
  const [selectedAnswerValue, setSelectedAnswerValue] = useState<number | null>(null);

  // Auth context for user ID
  const { user, updateUserField } = useAuth();

  // Timing references
  const testStartTime = useRef<number>(Date.now());
  const questionStartTime = useRef<number>(Date.now());
  const timerActiveStartTime = useRef<number>(0);
  const accumulatedTimerTime = useRef<number>(0);
  const questionActiveStartRef = useRef<number | null>(null);
  const questionAccumulatedTimeRef = useRef<number>(0);
  const hasCompletedRef = useRef<boolean>(false);
  const isFirstQuestionRef = useRef(true);

  // TTS integration
  const { playTTSWithCallback, isTTSPlaying, playChime } = useKTTS();

  // Empty count to fact mapping (empty counts 0-5 map to facts from K partwhole range)
  const emptyCountToFact = useCallback((emptyCount: number): string => {
    const [startFact] = TRACK_RANGES.TRACK_K_PARTWHOLE;
    return `FACT${startFact + emptyCount}`; // Maps 0-5 to facts 1679-1684
  }, []);

  // Determine fact status based on correctness and response time (targeting 6s fluency for part whole)
  const determineFactStatus = useCallback((isCorrect: boolean, timeSpentMs: number): FactStatus => {
    if (!isCorrect) {
      return 'learning';
    }

    const timeSpentSeconds = timeSpentMs / 1000;

    if (timeSpentSeconds > 6) {
      return 'accuracyPractice';  // Need to work on speed
    } else {
      return 'mastered';  // Already at target 6s fluency
    }
  }, []);

  // Generate random empty count (0-5 for K level part whole within 5)
  const generateRandomEmptyCount = useCallback(() => {
    return Math.floor(Math.random() * 6); // 0-5
  }, []);

  // Initialize first empty count (1-4 for first warmup to avoid confusing edge cases)
  useEffect(() => {
    if (currentEmptyCount === null) {
      // First warmup question: exclude 0 and 5 to avoid confusing edge cases
      const emptyCount = Math.floor(Math.random() * 4) + 1; // 1-4
      setCurrentEmptyCount(emptyCount);
    }
  }, [currentEmptyCount]);

  // Generate answer choices (target + 3 other options from 0-5)
  const generateAnswerChoices = useCallback((target: number) => {
    const choices = [target];
    const used = new Set([target]);

    // Add 3 other numbers between 0-5, avoiding target
    while (choices.length < 4) {
      const candidate = Math.floor(Math.random() * 6); // 0-5
      if (!used.has(candidate)) {
        choices.push(candidate);
        used.add(candidate);
      }
    }

    // Shuffle the choices
    return choices.sort(() => Math.random() - 0.5);
  }, []);

  const [answerChoices, setAnswerChoices] = useState<number[]>([]);

  // Update answer choices when empty count changes
  useEffect(() => {
    if (currentEmptyCount !== null) {
      setAnswerChoices(generateAnswerChoices(currentEmptyCount));
      setHasAnswered(false);
    }
  }, [currentEmptyCount, generateAnswerChoices]);

  // Set track ID and report page transition when component mounts
  useEffect(() => {
    sessionStorage.setItem('activeTrackId', 'TRACK_K_PARTWHOLE');

    if (user?.userId) {
      recordPageTransition({
        userId: user.userId,
        trackId: 'TRACK_K_PARTWHOLE',
        page: 'assessment'
      }).catch((error: any) => {
        console.error('Error recording page transition:', error);
      });
    }
  }, [user?.userId]);

  // Update question start time when moving to test phase
  useEffect(() => {
    if (!isWarmup) {
      questionStartTime.current = Date.now();
      accumulatedTimerTime.current = 0;
      timerActiveStartTime.current = 0;
      // Reset per-question timing
      questionAccumulatedTimeRef.current = 0;
    }
  }, [isWarmup, currentEmptyCount]);

  // Track timer active time
  useEffect(() => {
    if (isWarmup) return;

    if (timerActive) {
      timerActiveStartTime.current = Date.now();
      questionActiveStartRef.current = Date.now();
    } else {
      if (timerActiveStartTime.current > 0) {
        const activeTime = Date.now() - timerActiveStartTime.current;
        accumulatedTimerTime.current += activeTime;
        timerActiveStartTime.current = 0;
      }
      if (questionActiveStartRef.current) {
        const questionActiveTime = Date.now() - questionActiveStartRef.current;
        questionAccumulatedTimeRef.current += questionActiveTime;
        questionActiveStartRef.current = null;
      }
    }
  }, [timerActive, isWarmup]);

  // Wall clock timer countdown (strict 60s limit including VOs and transitions)
  useEffect(() => {
    // Don't run during warmup or before wall clock starts
    if (isWarmup || !wallClockStarted || wallClockTimeRemaining <= 0) {
      if (wallClockTimeRemaining <= 0 && wallClockStarted && user?.userId && !hasCompletedRef.current) {
        // Wall clock expired - end test immediately
        hasCompletedRef.current = true;
        handleTestComplete({
          userId: user.userId,
          testResults: [],
          recommendedStartSkill: 1,
          placementReason: 'Time limit reached (60 seconds)',
          completedAt: new Date(),
          totalQuestions,
          correctAnswers,
          activeTimeElapsed
        });
      }
      return;
    }

    const timer = setTimeout(() => {
      setWallClockTimeRemaining(prev => Math.max(0, prev - 100));
    }, 100);

    return () => clearTimeout(timer);
  }, [wallClockTimeRemaining, wallClockStarted, isWarmup, user?.userId, totalQuestions, correctAnswers, activeTimeElapsed]);

  // Active time tracking (for analytics - no limit)
  useEffect(() => {
    // Only track active time during test phase when timer is active
    if (isWarmup || !timerActive) return;

    const timer = setTimeout(() => {
      setActiveTimeElapsed(prev => prev + 100);
    }, 100);

    return () => clearTimeout(timer);
  }, [timerActive, isWarmup, activeTimeElapsed]);

  // Wrapper to handle page transition reporting before completing
  const handleTestComplete = useCallback(async (results: DiagnosticAssessmentResult) => {
    if (user?.userId) {
      try {
        await recordPageTransition({
          userId: user.userId,
          trackId: 'TRACK_K_PARTWHOLE',
          page: 'other'
        });
      } catch (error) {
        console.error('Error recording final page transition:', error);
      }

      // Save K assessment data to database and sync focusTrack
      try {
        const result = await completePreKAssessment(user.userId, 'TRACK_K_PARTWHOLE', {
          totalQuestions: results.totalQuestions || 0,
          correctAnswers: results.correctAnswers || 0,
          activeTimeElapsed: results.activeTimeElapsed || 0
        });
        if (result.focusTrack) {
          updateUserField({ focusTrack: result.focusTrack });
        }
      } catch (error) {
        console.error('Failed to save K assessment data:', error);
      }
    }
    onComplete(results);
  }, [user?.userId, onComplete, updateUserField]);

  // Play TTS when empty count is set and TTS hasn't played yet
  // Only play instruction on first warmup question or every 3rd test question (matching Subitizing logic)
  useEffect(() => {
    if (currentEmptyCount !== null && !ttsHasPlayed) {
      const shouldPlayAudio = isWarmup
        ? warmupCount === 0
        : currentQuestionIndex === 0;

      if (settings.speechEnabled && shouldPlayAudio) {
        playTTSWithCallback(K_QUESTION_SCREEN_AUDIO.partWhole.instruction, () => {
          if (!isWarmup) {
            setTimerActive(true);
            setQuestionTimerActive(true);
          }
          setTtsHasPlayed(true);
        }).catch(() => {
          if (!isWarmup) {
            setTimerActive(true);
            setQuestionTimerActive(true);
          }
          setTtsHasPlayed(true);
        });
      } else {
        // No audio needed or speech disabled - start timers immediately
        if (!isWarmup) {
          setTimerActive(true);
          setQuestionTimerActive(true);
        }
        setTtsHasPlayed(true);
      }
    }
  }, [currentEmptyCount, ttsHasPlayed, settings.speechEnabled, playTTSWithCallback, isWarmup, warmupCount, currentQuestionIndex]);

  // Reset state for new question
  const resetQuestionState = useCallback((callback?: () => void) => {
    isFirstQuestionRef.current = false;
    setTimerActive(false);
    setQuestionTimerActive(false); // Pause question timer during transitions
    setQuestionTimeRemaining(6000); // Reset question timer to 6 seconds
    setWrongAnswer(null);
    setTtsHasPlayed(false);
    setHasAnswered(false);
    setSelectedAnswerValue(null);

    // Execute callback after state reset if provided
    if (callback) {
      setTimeout(callback, 50);
    }
  }, []);

  // Generate next empty count with different value
  const generateNextEmptyCount = useCallback(() => {
    let newEmptyCount;
    do {
      newEmptyCount = generateRandomEmptyCount();
    } while (currentEmptyCount !== null && newEmptyCount === currentEmptyCount);

    return newEmptyCount;
  }, [currentEmptyCount, generateRandomEmptyCount]);

  // Store generateNextEmptyCount in a ref to avoid dependency issues
  const generateNextEmptyCountRef = useRef(generateNextEmptyCount);
  generateNextEmptyCountRef.current = generateNextEmptyCount;

  // Handle warmup progression
  const advanceWarmupQuestion = useCallback(() => {
    if (waitingForCorrectAnswer) {
      setWaitingForCorrectAnswer(false);
    }

    const newWarmupCount = warmupCount + 1;

    // Check if warmup is complete (fixed 3 questions)
    if (newWarmupCount >= 3) {
      // Show transition screen instead of immediately starting test
      setShowTransitionScreen(true);
      return;
    }

    setWarmupCount(newWarmupCount);
    resetQuestionState(() => setCurrentEmptyCount(generateNextEmptyCount()));
  }, [waitingForCorrectAnswer, warmupCount, generateNextEmptyCount, resetQuestionState]);

  // Handle transition screen completion - start the actual test
  const handleTransitionComplete = useCallback(() => {
    setShowTransitionScreen(false);
    setIsWarmup(false);
    setWarmupCount(0);
    setCurrentQuestionIndex(0);
    setWallClockStarted(true);
    resetQuestionState(() => setCurrentEmptyCount(generateNextEmptyCountRef.current()));
  }, [resetQuestionState]);

  // Handle test progression
  const advanceTestQuestion = useCallback(() => {
    if (waitingForCorrectAnswer) {
      setWaitingForCorrectAnswer(false);
      const nextIndex = currentQuestionIndex + 1;
      setCurrentQuestionIndex(nextIndex);
      resetQuestionState(() => setCurrentEmptyCount(generateNextEmptyCountRef.current()));
      return;
    }

    setCorrectAnswers(correctAnswers + 1);
    setTotalQuestions(totalQuestions + 1);
    const nextIndex = currentQuestionIndex + 1;
    setCurrentQuestionIndex(nextIndex);
    resetQuestionState(() => setCurrentEmptyCount(generateNextEmptyCount()));
  }, [waitingForCorrectAnswer, correctAnswers, totalQuestions, currentQuestionIndex, generateNextEmptyCount, resetQuestionState]);

  const handleAnswerClick = useCallback((selectedEmptyCount: number) => {
    if (currentEmptyCount === null) return;

    // If waiting for correct answer, only allow correct selection
    if (waitingForCorrectAnswer && selectedEmptyCount !== currentEmptyCount) return;

    // If not waiting and already answered, don't allow more clicks
    if (!waitingForCorrectAnswer && hasAnswered) return;

    // Force timer to be active since user has responded
    if (!isWarmup) {
      setTimerActive(true);
      setQuestionTimerActive(true); // Ensure question timer is active
    }
    setTtsHasPlayed(true);
    setHasAnswered(true);
    setSelectedAnswerValue(selectedEmptyCount);

    const isCorrect = selectedEmptyCount === currentEmptyCount;

    // Calculate per-question active response time for test questions
    let timerActiveResponseTime = 0;
    if (!isWarmup) {
      timerActiveResponseTime = questionAccumulatedTimeRef.current + (questionActiveStartRef.current ? Date.now() - questionActiveStartRef.current : 0);
    }

    // Report progress data for test questions
    if (!isWarmup && user?.userId && !waitingForCorrectAnswer) {
      try {
        const factId = emptyCountToFact(currentEmptyCount);
        const factStatus = determineFactStatus(isCorrect, timerActiveResponseTime);

        progressQueue.enqueueProgressUpdate(
          user.userId,
          {
            facts: {
              [factId]: {
                attempts: 1,
                correct: isCorrect ? 1 : 0,
                timeSpent: timerActiveResponseTime,
                status: factStatus
              }
            }
          }
        );
      } catch (error) {
        console.error('Error reporting K part whole progress:', error);
      }
    }

    // Handle answer logic with immediate audio feedback
    if (isWarmup) {
      if (isCorrect) {
        playChime().then(() => {
          setTimeout(() => {
            advanceWarmupQuestion();
          }, 300);
        });
      } else {
        setWrongAnswer(selectedEmptyCount);
        setTimeout(() => setWrongAnswer(null), 1000);

        // Replay instruction after boop
        if (settings.speechEnabled) {
          setTimeout(() => {
            playTTSWithCallback(K_QUESTION_SCREEN_AUDIO.partWhole.instruction).catch(() => {});
          }, 300);
        }

        setWaitingForCorrectAnswer(true);
        setHasAnswered(false); // Reset so user can try again
      }
    } else {
      // Quiz mode: neutral feedback with chime for all answers, same timing
      if (!waitingForCorrectAnswer && !isCorrect) {
        setTotalQuestions(totalQuestions + 1);
      }

      playChime().then(() => {
        setTimeout(() => {
          if (isCorrect) {
            advanceTestQuestion();
          } else {
            const nextIndex = currentQuestionIndex + 1;
            setCurrentQuestionIndex(nextIndex);
            resetQuestionState(() => setCurrentEmptyCount(generateNextEmptyCountRef.current()));
          }
        }, 300); // 300ms delay for all answers
      });
    }
  }, [
    currentEmptyCount, waitingForCorrectAnswer, hasAnswered, isWarmup, timerActive,
    user, isTTSPlaying, settings.speechEnabled, emptyCountToFact, determineFactStatus,
    playChime, playTTSWithCallback, advanceWarmupQuestion, advanceTestQuestion,
    totalQuestions, currentQuestionIndex, resetQuestionState
  ]);

  // 6-second per-question countdown timer
  useEffect(() => {
    // Don't run question timer during warmup, when not active, or when already answered
    if (isWarmup || !questionTimerActive || questionTimeRemaining <= 0 || hasAnswered) {
      if (questionTimeRemaining <= 0 && questionTimerActive && !isWarmup && !hasAnswered) {
        // Question time expired - auto advance without showing correct answer
        if (waitingForCorrectAnswer) {
          // If waiting for correct answer, just advance
          setWaitingForCorrectAnswer(false);
          const nextIndex = currentQuestionIndex + 1;
          setCurrentQuestionIndex(nextIndex);
          resetQuestionState(() => setCurrentEmptyCount(generateNextEmptyCountRef.current()));
        } else {
          // Count as an incorrect attempt and advance
          setTotalQuestions(totalQuestions + 1);

          // Report progress data for timeout (treat as incorrect answer)
          if (user?.userId && currentEmptyCount !== null) {
            try {
              const factId = emptyCountToFact(currentEmptyCount);
              const factStatus = determineFactStatus(false, 6000); // Use full question time as timeout response time

              progressQueue.enqueueProgressUpdate(
                user.userId,
                {
                  facts: {
                    [factId]: {
                      attempts: 1,
                      correct: 0, // Timeout is incorrect
                      timeSpent: questionAccumulatedTimeRef.current + (questionActiveStartRef.current ? Date.now() - questionActiveStartRef.current : 0), // Actual time spent on this question
                      status: factStatus
                    }
                  }
                }
              );
            } catch (error) {
              console.error('Error reporting K part whole timeout progress:', error);
            }
          }

          const nextIndex = currentQuestionIndex + 1;
          setCurrentQuestionIndex(nextIndex);
          resetQuestionState(() => setCurrentEmptyCount(generateNextEmptyCountRef.current()));
        }
      }
      return;
    }

    const timer = setTimeout(() => {
      setQuestionTimeRemaining(prev => prev - 100);
    }, 100);

    return () => clearTimeout(timer);
  }, [questionTimeRemaining, questionTimerActive, isWarmup, waitingForCorrectAnswer, hasAnswered]);

  if (currentEmptyCount === null) {
    return <div className="min-h-screen bg-gradient-to-br from-purple-100 via-pink-50 to-blue-100 flex items-center justify-center">
      <div className="text-2xl">Loading...</div>
    </div>;
  }

  // Show transition screen after warmup completes
  if (showTransitionScreen) {
    return (
      <WarmupTransitionScreen
        onComplete={handleTransitionComplete}
        speechEnabled={settings.speechEnabled}
      />
    );
  }

  // Calculate filled count (5 - empty count)
  const filledCount = 5 - currentEmptyCount;

  return (
    <div className="h-screen overflow-hidden bg-gradient-to-br from-purple-100 via-pink-50 to-blue-100 flex flex-col">
      {/* Header */}
      <AssessmentHeader
        isWarmup={isWarmup}
        showTimer={true}
        wallClockTimeRemaining={wallClockTimeRemaining}
        wallClockStarted={wallClockStarted}
        isTTSPlaying={isTTSPlaying}
        timerHideable={true}
      />

      {/* Listen Button */}
      {settings.speechEnabled && (
        <div className="flex justify-center pt-4">
          <ListenButton
            onClick={() => playTTSWithCallback(K_QUESTION_SCREEN_AUDIO.partWhole.instruction)}
            disabled={hasAnswered}
            isPlaying={isTTSPlaying}
          />
        </div>
      )}

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-4 pb-8 gap-8">
        {/* 5x1 Grid of Balls - Always visible */}
        <div className="flex gap-6 lg:gap-8">
          {[0, 1, 2, 3, 4].map((index) => (
            <motion.div
              key={index}
              initial={isFirstQuestionRef.current ? { scale: 0, opacity: 0 } : { opacity: 0 }}
              animate={isFirstQuestionRef.current ? { scale: 1, opacity: 1 } : { opacity: 1 }}
              transition={isFirstQuestionRef.current ? { duration: 0.3, delay: index * 0.1 } : { duration: 0.15 }}
              className={`
                w-16 h-16 lg:w-20 lg:h-20 rounded-full shadow-lg border-4
                ${index < filledCount
                  ? 'bg-blue-400 border-blue-500'
                  : 'bg-transparent border-dashed border-gray-400'
                }
              `}
            />
          ))}
        </div>

        {/* Answer Choices - 2x2 grid */}
        <AnswerChoicesGrid
          choices={answerChoices}
          correctAnswer={currentEmptyCount}
          wrongAnswer={wrongAnswer}
          hasAnswered={hasAnswered}
          waitingForCorrectAnswer={waitingForCorrectAnswer}
          disabled={false}
          onChoiceClick={handleAnswerClick}
          selectedAnswer={selectedAnswerValue}
          neutralFeedbackMode={!isWarmup}
          minimalAnimation={!isFirstQuestionRef.current}
        />
      </div>
    </div>
  );
};

export default PartWholeQuestionScreen;
